import React, { useEffect, useState } from "react";
import { Input, toAbsoluteUrl } from "sr/helpers";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import { registerCorporate } from "sr/utils/api/corporate/registerCorporate";
import corporateRegisterSchema from "sr/utils/schemas/corporate/corporateRegisterSchema";
import { sendOTP } from "sr/utils/api/sendOTP";
import { login } from "sr/utils/api/login";
import { ButtonAuth } from "sr/helpers/ui-components/ButtonAuth";
import { useAuth } from "app/pages/module/auth";
import { InputNumberFormat } from "sr/helpers/functions/InputNumberFormat";
import { ReactSelect } from "sr/helpers/ui-components/ReactSelect";
import { CreateWallet } from "sr/utils/api/wallet/CreateWallet";
import { scrollToError } from "sr/helpers/functions/scrollToError";
import { getBusinessType } from "sr/utils/api/masterData/getBusinessType";

export default function RegisterCorporate() {
  const navigate = useNavigate();
  const intl = useIntl();

  const [corporateName, setCorporateName] = useState();
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();
  const [email, setEmail] = useState();
  const [mobileNo, setMobileNo] = useState();
  const [corporateType, setCorporateType] = useState();
  const [loader, setLoader] = useState(false);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<any[]>([]);

  const { persistToken } = useAuth();

  const initialValues = {
    username: username,
    password: password,
    email: email,
    mobile_number: mobileNo,
    corporate_name: corporateName,
    corporate_type: corporateType,
  };

  const { errors, submitForm } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: corporateRegisterSchema,
    onSubmit: async (values) => {
      try {
        setLoader(true);
        const result = await registerCorporate(values);

        if (result?.success === true) {
          await sendOTP({ username: result?.data?.username });

          const loginResult = await login({
            username: username,
            password: password,
          });

          setLoader(false);

          if (loginResult?.success === true) {
            const authData = {
              userType: 'corporate',
              user_id: loginResult?.data?.auth.user_id,
            };
            localStorage.setItem("auth", JSON.stringify(authData));
            persistToken(loginResult?.data?.auth.jwt);

            await CreateWallet({
              owner_id: loginResult?.data?.auth.user_id,
              owner_type: "CORPORATE",
            });

            navigate("/verify-otp");
          }
        } else {
          setLoader(false);
        }
      } catch (e) {
        console.error("Corporate register error:", e);
        setLoader(false);
      }
    },
  });

  useEffect(() => {
    scrollToError(errors);
  }, [errors]);

  useEffect(() => {
    (async () => {
      try {
        const bt = await getBusinessType();
        if (bt?.data) {
          setBusinessTypeOptions(
            bt.data.map((i: any) => ({
              value: i.value,
              label: intl.formatMessage({ id: i.label, defaultMessage: i.label }),
            }))
          );
        }
      } catch (e) {
        console.error("Failed to load business types:", e);
      }
    })();
  }, [intl]);

  return (
    <main>
      <div className="lg:container mx-auto lg:max-w-3xl lg:mt-24 mt-8 px-4 lg:px-0">
        <div className="lg:flex block">
          <div className="lg:flex-1 lg:flex block w-full flex-col h-fit lg:mx-12 mx-auto lg:p-6 px-4 py-4 bg-white border border-gray-border rounded-xl">
            <div className="flex justify-center">
              <Link to="/corporate/register" className="z-50">
                <img
                  width={150}
                  src={toAbsoluteUrl("/media/logos/logo2.png")}
                  alt=""
                  className="bg-white rounded-lg"
                />
              </Link>
            </div>
            <h1 className="text-xl mt-10">
              <FormattedMessage
                id="ECOMMERCE.COMMON.CREATE_AN_ACCOUNT.TEXT"
                defaultMessage="Create an Account"
              />
            </h1>
            <h3 className="text-md">As Corporate</h3>

            <div className="w-full">
              <form>
                <Input
                  onChange={(e) => setCorporateName(e.target.value)}
                  error={errors.corporate_name}
                  value={corporateName}
                  label="Name"
                  placeholder="Enter your Corporate Name"
                  type="text"
                />

                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  error={errors.username}
                  label="Username"
                  placeholder="Enter your username"
                  type="text"
                />

                <Input
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  error={errors.password}
                  label="Password"
                  placeholder="Enter your password"
                  type="password"
                />

                <Input
                  onChange={(e) => setMobileNo(e.target.value)}
                  value={InputNumberFormat(mobileNo)}
                  error={errors.mobile_number}
                  label="Mobile Number"
                  placeholder="Enter your mobile number"
                  type="tel"
                />

                <Input
                  error={errors.email}
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  label="Email Address"
                  placeholder="Enter your email"
                  type="email"
                />

                <ReactSelect
                  label={intl.formatMessage({ id: "RECRUITER.REGISTER.INPUT.SELECT.BUSSINESS.TYPE", defaultMessage: "Select Business Type" })}
                  placeholder={intl.formatMessage({ id: "RECRUITER.REGISTER.INPUT.CHOOSE.BUSSINESS.TYPE", defaultMessage: "Choose Business Type" })}
                  type="text"
                  onChange={(selected) => setCorporateType(selected?.value)}
                  error={errors.corporate_type}
                  value={corporateType}
                  isMulti={false}
                  options={businessTypeOptions}
                />
              </form>

              <div className="flex justify-center">
                <ButtonAuth
                  loader={loader}
                  onClick={() => {
                    submitForm();
                    localStorage.setItem("RegisterAs", "Corporate");
                  }}
                >
                  <FormattedMessage
                    id="AUTH.GENERAL.CREATE.ACCOUNT.BUTTON"
                    defaultMessage="Create Account"
                  />
                </ButtonAuth>
              </div>

              <div className="mt-10 space-x-1 flex justify-center">
                <span>
                  <FormattedMessage
                    id="AUTH.ALREADY.HAVE.ACCOUNT"
                    defaultMessage="Already have an account?"
                  />
                </span>
                <Link
                  to="/corporate/login"
                  className="cursor-pointer hover:text-pink underline"
                  replace
                >
                  <FormattedMessage
                    id="AUTH.SIGN_IN_HERE"
                    defaultMessage="Sign in here"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
